import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useAuth } from './useAuth';
import { getIdTokenResult } from 'firebase/auth';
import { OFFLINE_ENABLED } from '../offline/entities';
import { isEffectivelyOnline } from '../offline/connectivity';
import { cacheClaims, getCachedClaims } from './offlineAuthGrace';
import {
  clearIdentity,
  fetchMePermissions,
  getIdentityState,
  getPermissionState,
  publishCachedPermissions,
  publishIdentity,
  publishPermissionsUnavailable,
  subscribeIdentityState,
  subscribePermissionState,
  PermissionFailureClass,
} from './mePermissions';

export interface UserClaims {
  /**
   * The five values of `enum UserRole` in `backend/prisma/schema.prisma`. CSR
   * joined the union one release BEFORE any user could hold it, and has been
   * assignable since 2026-09-21 (#65 — `CSR_ROLE_ASSIGNABLE=false` is now the
   * emergency OFF). Widening early was the point: this field is a bare string on
   * the wire — the ID token's custom claim, and the offline cache's re-read of
   * it (:185) — so the web must NAME a role before it ever meets one.
   *
   * `auth/roles.ts` turns this union into the two label maps, and the
   * `Record<UserRoleSlug, …>` there is what makes a role with no label a COMPILE
   * error rather than a blank chip — the web's half of the totality proof
   * INV-103 makes on the backend for `ROLE_DEFAULTS`.
   */
  role: 'OWNER' | 'ADMIN' | 'VETERINARIAN' | 'VETERINARY_TECHNICIAN' | 'CSR';
  clinicId: string;
  permissions: string[];
}

/** Identity half of the claims — what the ID token alone decides. */
type UserIdentity = Pick<UserClaims, 'role' | 'clinicId'>;

/** One frozen array for the fail-closed case, so `claims` keeps a stable identity
 *  across renders (consumers memoize on it). */
const NO_PERMISSIONS: string[] = [];

const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

// Synthetic claims used in DEV_BYPASS mode — mirrors the backend's jwt.strategy
// bypass path so role/permission gates in the UI behave as an OWNER.
//
// The clinic id MUST match the backend's DEV_BYPASS_CLINIC_ID (jwt.strategy.ts,
// same 'dev-clinic' default). Screens that build a URL from the claim — e.g.
// GET /clinics/<claimId> on HorseProfileModern and BillingSettings — get a 403
// from clinics.controller when the two disagree, because the controller rejects
// any id that is not the caller's own clinic. Point both at the seeded clinic.
//
// ⚠ ENUMERATED, AND NOW EXACT. This is a verbatim copy of `PERMISSIONS` in
// `backend/src/auth/permissions.ts` — which after the P3 sweep is precisely what
// `ROLE_DEFAULTS.OWNER` holds, because no role holds a catch-all any more. The
// frontend cannot import across the tree, so `useRBAC.test.tsx` re-reads that file
// and asserts EQUALITY (delete the drift guard and slug #31 silently never reaches
// a dev session, or a retired grant creeps back into a role's defaults unseen).
//
// The dev identity is no longer a CANARY for anything — P2 deliberately held it one
// notch narrower than a real OWNER so a dark affordance here named a gate P3 still
// had to re-point, and that list is now empty. Every screen that mirrored a coarse
// grant asks for a granular slug an OWNER holds: `components/InvoiceDetail.tsx`
// (the payer/split action → manage:charges), `pages/Inventory/index.tsx` (the
// pricing prompt → manage:inventory_pricing), `PatientFiles/PatientFilesTab.tsx`
// (the attachment DELETE → manage:medical_records_archive) and both HorseProfile
// screens (→ manage:patients / manage:medical_records_archive / manage:charges,
// replacing the phantoms `manage:horses` and `delete:medical-records`, which were
// never in any vocabulary and only ever resolved through `manage:all`).
const DEV_BYPASS_PERMISSIONS: string[] = [
  'manage:users',
  'manage:roles_permissions',
  'view:patients',
  'manage:patients',
  'view:medical_records',
  'manage:medical_records',
  'manage:medical_records_finalize',
  'manage:medical_records_archive',
  'manage:expired_lot_doses',
  'view:clients',
  'manage:clients',
  'view:appointments',
  'manage:appointments',
  'view:billing',
  'manage:charges',
  'manage:payments',
  'manage:discounts',
  'manage:refunds',
  'manage:pricing',
  // The `Reports` category (ADR-0035 A8, 2026-09-21). ORDER MATTERS: useRBAC.test.tsx's cross-tree
  // drift guard compares this array against backend `PERMISSIONS` with `toEqual`, so these two sit
  // after `manage:pricing` exactly as they do there — `view:financial_reports` MOVED out of the
  // Billing block when it moved category.
  'view:reports',
  'view:financial_reports',
  'view:inventory',
  'manage:inventory',
  'manage:inventory_adjustments',
  'manage:inventory_pricing',
  'manage:controlled_substances',
  'view:discharge_summaries',
  'manage:discharge_summaries',
  'manage:practice_settings',
  'manage:clinical_settings',
  'manage:integrations',
  'manage:subscription',
];

// ⚠ `role` DEFAULTS to OWNER here, so this mirror assumes the backend is on its DEV_BYPASS_ROLE
// DEFAULT of OWNER too. Run the API with `DEV_BYPASS_ROLE=VETERINARIAN` (or any other non-OWNER)
// and leave the web on its default, and the web dev session is strictly WIDER than the API: every
// `hasRole('OWNER')`/`hasRole('ADMIN')` affordance and every OWNER-only slug above still renders,
// over requests the backend 403s. Dev-only, and nothing carries the bypass role to the browser, so
// the two are kept in step BY HAND — set both, to the same value.
//
// ⚠ AND THE SAME HOLDS FOR THE SET, which is why `REACT_APP_DEV_BYPASS_PERMISSIONS` exists
// (INV-108). Hardcoding the full OWNER list made the dev-bypass session the one identity that can
// never be refused, so the screens a narrowed person meets — "You don't have access to view
// clients" — were unwalkable locally, and INV-107 trap (a) is invisible here for the same family
// of reason (the identity is seeded synchronously below, so there is no first paint without it).
// Comma-separated slugs; an EMPTY value is a person who holds NOTHING, which is the most useful
// case to look at, so the override binds on the variable being DEFINED rather than non-empty.
// Both vars are read ONLY under dev-bypass, and no deployed build sets any of the three.
const devBypassOverride = <T,>(value: string | undefined, parse: (raw: string) => T): T | null =>
  DEV_BYPASS && value !== undefined ? parse(value) : null;

export const DEV_CLAIMS: UserClaims = {
  role:
    devBypassOverride(
      process.env.REACT_APP_DEV_BYPASS_ROLE,
      (raw) => raw.trim() as UserClaims['role'],
    ) || 'OWNER',
  clinicId: process.env.REACT_APP_DEV_BYPASS_CLINIC_ID || 'dev-clinic',
  permissions:
    devBypassOverride(process.env.REACT_APP_DEV_BYPASS_PERMISSIONS, (raw) =>
      raw
        .split(',')
        .map((slug) => slug.trim())
        .filter(Boolean),
    ) ?? DEV_BYPASS_PERMISSIONS,
};

export const useRBAC = () => {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  // BOTH halves of the answer are shared module state (see mePermissions' two headers): the
  // effective permission SET, because ~45 consumers render it and a screen that contradicts
  // itself is worse than either answer — and, since the no-access fix wave, the IDENTITY too.
  // It was per-instance state, so every consumer began at `claims === null` until its OWN token
  // round trip resolved: an Add button that pops in on every page visit, a blank Dashboard, two
  // serial refreshes to enter a gated screen, and (INV-107) a `!loading && claims === null`
  // window that a verdict must never read as a refusal.
  const permissionState = useSyncExternalStore(subscribePermissionState, getPermissionState);
  const identityState = useSyncExternalStore(subscribeIdentityState, getIdentityState);
  // Uid-gated exactly as the set is: the store is cleared on session end, but a surviving
  // snapshot must never be able to answer for a different person.
  const sharedIdentity =
    !DEV_BYPASS && uid !== null && identityState.uid === uid ? identityState.identity : null;
  /**
   * Has THIS instance's own round trip finished, and for whom?
   *
   * `null` is "not yet", which is what a fresh mount starts at — and it must be distinguishable
   * from "settled for a null user", or a signed-out shell would report `loading: false` one
   * render earlier than it used to. It carries the uid so a user CHANGE re-opens the window
   * without an extra render: state does not reset on a prop change, and comparing the stored uid
   * to the current one is what makes it look as if it did.
   */
  const [settled, setSettled] = useState<{ uid: string | null } | null>(null);
  const identity: UserIdentity | null = DEV_BYPASS ? DEV_CLAIMS : sharedIdentity;
  // "Do we know who this is yet." A warm store answers it on the FIRST render — that is the whole
  // point of publishing the identity — and otherwise it stays open until this instance settles.
  const loading = DEV_BYPASS
    ? false
    : sharedIdentity === null && (settled === null || settled.uid !== uid);

  useEffect(() => {
    // Cancellation. This effect awaits TWO round trips (the ID token, then
    // /users/me), and logout() runs clearClaimsCache → signOut → endSession →
    // clearClaimsCache locally in milliseconds. Without this guard a response
    // landing after that wrote the DEPARTED identity's uid/role/clinicId/
    // permissions back into st.offline.claims.v2 — where it survives on a shared
    // device — and re-applied the old identity to any still-mounted consumer.
    // Authz metadata must not outlive the session.
    let ignore = false;
    // In dev-bypass mode there is no real Firebase ID token to parse — the
    // mock user's getIdToken() returns the literal 'dev-bypass-token', which
    // Firebase's getIdTokenResult() rejects as malformed JWT. Use synthetic
    // claims instead.
    if (DEV_BYPASS) return;

    const fetchClaims = async () => {
      if (!user) {
        // Signed out (or not signed in yet). Clear the shared identity: it names a clinic, and
        // authz metadata must not outlive the session — `resetMePermissions` covers the logout
        // path, this covers every other way `user` becomes null.
        clearIdentity();
        setSettled({ uid: null });
        return;
      }

      // ── 1. the TOKEN decides the identity, and nothing waits for it ──────────
      let tokenIdentity: UserIdentity;
      try {
        // A5 (offline mode only): force-refresh ONLY when online. Offline,
        // forceRefresh=true forces a network token exchange that throws
        // (network-request-failed), which would null the claims (and thus
        // clinicId) and break every offline replica read; forceRefresh=false
        // returns the cached (non-expired) token's claims with no network call.
        // With the flag OFF (prod default) keep the original unconditional
        // force-refresh so claim freshness is byte-identical to pre-A5.
        // isEffectivelyOnline (not bare navigator.onLine): virtual adapters
        // keep onLine stale-true, which would force a doomed token exchange.
        const forceRefresh = OFFLINE_ENABLED ? isEffectivelyOnline() : true;
        const tokenResult = await getIdTokenResult(user, forceRefresh);
        // Role + clinicId only. Tokens minted before P4 may still carry a permission array for up
        // to an hour; it is deliberately unread because Postgres is the sole live authority.
        const customClaims = tokenResult.claims as {
          role: UserClaims['role'];
          clinicId: string;
        };
        tokenIdentity = { role: customClaims.role, clinicId: customClaims.clinicId };
      } catch (error) {
        if (ignore) return;
        // Offline (or a transient token error): fall back to this user's last
        // cached claims so clinicId survives and the replica stays readable.
        // Flag OFF → no cache, so this is byte-identical to the pre-A5 null set.
        const cached = OFFLINE_ENABLED ? getCachedClaims(user.uid) : null;
        if (cached) {
          // `CachedClaims.role` is a bare string — localStorage is untrusted input,
          // so it is typed as what it is. The cast is the same one the pre-split
          // code made (`cached as UserClaims`); the backend re-resolves the role on
          // every request, so a tampered value buys a UI affordance and a 403.
          publishIdentity(user.uid, {
            role: cached.role as UserClaims['role'],
            clinicId: cached.clinicId,
          });
          // The permission read never ran, so publish the cached set here or the
          // store would hold nothing and every consumer would fail closed against
          // a cache that DID answer.
          publishCachedPermissions(user.uid, cached.permissions);
        } else if (getIdentityState().uid === user.uid && getIdentityState().identity) {
          // TRANSIENT, and the store already knows this person — keep them. This mirrors
          // `publishPermissionsUnavailable`'s transient arm exactly, and for the same reason: one
          // dropped token refresh is not evidence about WHO somebody is, and discarding a known
          // identity here would blank `clinicId` for every mounted consumer (the offline replica
          // provider among them) over a blip another instance already answered.
          //
          // Nothing is published — the identity that is already there is the answer.
        } else {
          console.error('Error fetching user claims:', error);
          // IDENTITY UNKNOWN: no token, no cache, nothing in the store. We publish nothing and
          // clear nothing — there is no answer to give and no failure to report either, because
          // the TOKEN failed, not the permission read. `loading` closes (we have stopped asking)
          // and `claims` stays null, which is the third state INV-107 names: it must decide
          // nothing. `screenAccessVerdict` answers `unknown`, the menu stays whole, and every
          // control renders, because the server is the authority and it has not refused anybody.
        }
        setSettled({ uid: user.uid });
        return;
      }

      if (ignore) return;
      // R-F / INV-19: publish role + clinicId NOW. They are hostage to nothing
      // below — the axios instance has a 30 s timeout and the transport adds a
      // 250 ms-delayed retry, so waiting for the permission read could hold a
      // blank /reports and an unmountable offline replica provider for ~60 s on a
      // blackholed uplink. `loading` answers the same question ("do we know who
      // this is yet") and clears with them.
      publishIdentity(user.uid, tokenIdentity);
      setSettled({ uid: user.uid });

      // ── 2. the PERMISSIONS, which only ever add to that ─────────────────────
      // Known-offline: don't ask. The read would be two doomed requests plus the
      // transport's 250 ms retry gap before the cached set is used, and offline the
      // cache IS the answer. Flag-gated so prod (offline OFF) keeps behaving exactly
      // as it does today — there is no cache to serve there anyway.
      if (OFFLINE_ENABLED && !isEffectivelyOnline()) {
        const cached = getCachedClaims(user.uid);
        if (cached) publishCachedPermissions(user.uid, cached.permissions);
        // No cached set offline: fail CLOSED and say so. 'transient' is the honest
        // class — reconnecting really does fix it.
        else publishPermissionsUnavailable(user.uid, 'transient');
        return;
      }

      // P2 / INV-104: the effective set comes from GET /users/me, which the backend
      // resolves from Postgres per request. The ID token is a ~1-hour snapshot
      // force-refreshed only on mount, so reading them there kept a revoked grant
      // visible in the UI for up to an hour while the API 403'd. Every arm below
      // publishes into the shared store, so all ~45 consumers and the one banner
      // render the same answer.
      try {
        const permissions = await fetchMePermissions(user.uid);
        if (ignore) return;
        // Cache (uid-keyed) so an offline re-run / remount can recover the
        // clinicId+role+permissions instead of falling to null. Authz metadata
        // only (not PHI). OFFLINE-ONLY: with the flag off, write nothing (no new
        // prod artifact) — cleared by clearReplica/endSession/logout, all
        // flag-gated too. ⚠ Reached only when the read ANSWERED: writing the
        // fail-closed empty set would destroy the very fallback the next offline
        // mount needs.
        if (OFFLINE_ENABLED) cacheClaims({ uid: user.uid, ...tokenIdentity, permissions });
      } catch {
        // Offline, /users/me failed twice (the transport already retried once), or it answered
        // without the permissions key. There is no token fallback after P4. Prefer
        // this user's last cached set; with none, fail CLOSED in the UI (no
        // affordances) while role+clinicId survive so the offline replica stays
        // readable. The backend is the authority in both directions. Fail-closed is
        // SAID OUT LOUD: `fetchMePermissions` has already published `unavailable`
        // plus its failure class, and a cached set replaces that below.
        // Not logged here: `mePermissions` logs the failed READ once, and this
        // hook has ~45 consumers sharing it — logging per consumer printed ~45
        // identical lines for one blip.
        const cached = OFFLINE_ENABLED ? getCachedClaims(user.uid) : null;
        if (ignore || !cached) return;
        publishCachedPermissions(user.uid, cached.permissions);
      }
    };

    fetchClaims();
    return () => {
      ignore = true;
    };
  }, [user]);

  // ── the shared answer, narrowed to THIS user ───────────────────────────────
  // Every read is uid-gated: the store is cleared on session end, but a surviving
  // snapshot must never be able to answer for a different identity.
  const permissions = useMemo<string[]>(() => {
    if (DEV_BYPASS) return DEV_CLAIMS.permissions;
    if (!uid || permissionState.uid !== uid || permissionState.permissions === null) {
      return NO_PERMISSIONS;
    }
    return permissionState.permissions;
  }, [uid, permissionState]);

  const claims = useMemo<UserClaims | null>(
    () => (identity ? { ...identity, permissions } : null),
    [identity, permissions],
  );

  // "We could not READ your permissions" — NOT "you have none". Both render as an
  // empty set and hide every gated affordance, so without this flag a single
  // failed /users/me is indistinguishable from a genuinely empty grant, and the
  // only trace is a console.error. The shell surfaces it
  // (auth/PermissionsUnavailableBanner, mounted in App.tsx), which is also why the
  // flag and its CLASS come from the shared store: the banner is rendered by ONE
  // instance and must speak for all of them.
  // ⚠ It does NOT imply an empty set. A TRANSIENT failure keeps whatever answer the
  // store already held for this uid (mePermissions' publishPermissionsUnavailable),
  // so this can be true while every affordance still renders — "not re-checked",
  // which is what the banner's transient copy says. Only a DEFINITIVE refusal (or a
  // first read that never answered) leaves `permissions` null and closes the UI.
  const permissionsUnavailable =
    !DEV_BYPASS && uid !== null && permissionState.uid === uid && permissionState.unavailable;
  const permissionsFailure: PermissionFailureClass | null = permissionsUnavailable
    ? permissionState.failure
    : null;
  /**
   * The set has not been ANSWERED for this user yet — as opposed to answered
   * "nothing". Anything that would otherwise render a refusal it has not earned
   * (the assistant's composer placeholder) can say "checking" instead. Deliberately
   * NOT the banner's condition: a read still in flight is not a failure.
   */
  const permissionsPending = !DEV_BYPASS && uid !== null && permissionState.uid !== uid;

  // Stable identities (per claims) — consumers memoize on these (e.g. the
  // Settings nav) and effects keyed on them must not re-fire every render.
  const hasRole = useCallback(
    (role: UserClaims['role']) => {
      return claims?.role === role;
    },
    [claims],
  );

  const hasPermission = useCallback(
    (permission: string) => {
      if (!claims) return false;
      // EXACT membership, nothing else. P2 honoured 'view:all'/'manage:all' as
      // prefix wildcards because 19 live backend gates still REQUIRED them and
      // every role still HELD them; P3 re-points those gates and drops the
      // catch-alls from ROLE_DEFAULTS, and deletes all three copies of the rule in
      // one commit: here, `backend/src/auth/role.guard.ts`, and
      // `backend/src/modules/ai-assistant/agent/access.ts`. Keeping it would be a
      // forgery — a pre-P3 token, a rolled-back backend or a stale cache blob
      // naming 'view:all' would light up affordances the API now refuses.
      return claims.permissions.includes(permission);
    },
    [claims],
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]) => {
      return permissions.some(permission => hasPermission(permission));
    },
    [hasPermission],
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]) => {
      return permissions.every(permission => hasPermission(permission));
    },
    [hasPermission],
  );

  return {
    claims,
    loading,
    permissionsUnavailable,
    permissionsFailure,
    permissionsPending,
    hasRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};
